import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import { Event, UserProfile } from '../types';

const EventTransferPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  const loadEvent = React.useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await apiClient.get(`/v1/events/${eventId}`);
      setEvent(response.data.data);
    } catch (err: any) {
      console.error('Failed to load event:', err);
      setLoadError(err.response?.data?.error?.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (userRole !== 'admin') {
      navigate('/unauthorized');
      return;
    }
    loadEvent();
  }, [eventId, userRole, navigate, loadEvent]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }

    try {
      setSearching(true);
      const response = await apiClient.get('/v1/users/search', {
        params: { query: searchQuery }
      });
      setSearchResults(response.data.data || []);
    } catch (err: any) {
      console.error('Search failed:', err);
      alert(err.response?.data?.error?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedUser) {
      return;
    }

    try {
      setTransferring(true);
      setTransferError(null);

      await apiClient.post(`/v1/events/${eventId}/transfer`, {
        new_owner_id: selectedUser.user_id
      });

      navigate(`/events/${eventId}`);
    } catch (err: any) {
      console.error('Failed to transfer ownership:', err);
      setTransferError(
        err.response?.data?.error?.message || 'Failed to transfer ownership. Please try again.'
      );
      setTransferring(false);
      setShowConfirmModal(false);
    }
  };

  if (userRole !== 'admin') {
    return null;
  }

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading event...</p>
        </div>
      </div>
    );
  }

  if (loadError || !event) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {loadError || 'Event not found'}
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/events')}
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Events
        </button>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-lg-8 offset-lg-2">
          <button
            className="btn btn-link text-decoration-none mb-3"
            onClick={() => navigate(`/events/${eventId}`)}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Event
          </button>

          <div className="card border-danger">
            <div className="card-header bg-danger text-white">
              <h4 className="mb-0">
                <i className="fas fa-exchange-alt me-2"></i>
                Transfer Event Ownership
              </h4>
            </div>

            <div className="card-body">
              <div className="alert alert-warning">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Admin Action:</strong> This will transfer ownership of the event to another user.
                The current owner will lose creator privileges.
              </div>

              {transferError && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {transferError}
                </div>
              )}

              {/* Current Event Info */}
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="card-title">Event Details</h5>
                  <div className="mb-2">
                    <strong>Event Name:</strong> {event.name}
                  </div>
                  <div className="mb-2">
                    <strong>Current Owner:</strong> {event.creator_name || 'Unknown'}
                  </div>
                  <div className="mb-2">
                    <strong>Location:</strong> {event.location}
                  </div>
                  <div>
                    <strong>Dates:</strong> {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* User Selection */}
              <div className="mb-4">
                <h5>Select New Owner</h5>

                {!selectedUser ? (
                  <>
                    <div className="mb-3">
                      <label htmlFor="search" className="form-label">
                        Search for User
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                          placeholder="Search by name or email..."
                          disabled={searching}
                        />
                        <button
                          className="btn btn-primary"
                          onClick={handleSearch}
                          disabled={searching || !searchQuery.trim()}
                        >
                          {searching ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="fas fa-search"></i>
                          )}
                        </button>
                      </div>
                    </div>

                    {searchResults.length > 0 && (
                      <div className="list-group">
                        {searchResults.map((user) => (
                          <button
                            key={user.user_id}
                            className="list-group-item list-group-item-action d-flex align-items-center"
                            onClick={() => setSelectedUser(user)}
                            disabled={user.user_id === event.creator_id}
                          >
                            {user.profile_image_url ? (
                              <img
                                src={user.profile_image_url}
                                alt={user.name}
                                className="rounded-circle me-3"
                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                              />
                            ) : (
                              <i className="fas fa-user-circle fa-2x text-secondary me-3"></i>
                            )}
                            <div className="flex-grow-1">
                              <h6 className="mb-0">{user.name}</h6>
                              <small className="text-muted">{user.email}</small>
                            </div>
                            {user.user_id === event.creator_id && (
                              <span className="badge bg-secondary">Current Owner</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {selectedUser.profile_image_url ? (
                            <img
                              src={selectedUser.profile_image_url}
                              alt={selectedUser.name}
                              className="rounded-circle me-3"
                              style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                            />
                          ) : (
                            <i className="fas fa-user-circle fa-3x text-secondary me-3"></i>
                          )}
                          <div>
                            <h6 className="mb-0">{selectedUser.name}</h6>
                            <small className="text-muted">{selectedUser.email}</small>
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setSelectedUser(null)}
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate(`/events/${eventId}`)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setShowConfirmModal(true)}
                  disabled={!selectedUser}
                >
                  <i className="fas fa-exchange-alt me-2"></i>
                  Transfer Ownership
                </button>
              </div>
            </div>
          </div>

          {/* Audit Log Info */}
          <div className="card mt-3">
            <div className="card-body">
              <h6 className="text-muted mb-2">
                <i className="fas fa-info-circle me-2"></i>
                Audit Information
              </h6>
              <p className="mb-0 small text-muted">
                This ownership transfer will be logged in the audit log for security and compliance purposes.
                The action will include the admin user, original owner, new owner, and timestamp.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedUser && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Confirm Ownership Transfer
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={transferring}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to transfer ownership of <strong>{event.name}</strong>?
                </p>
                <div className="card bg-light">
                  <div className="card-body">
                    <div className="mb-2">
                      <strong>From:</strong> {event.creator_name || 'Unknown'}
                    </div>
                    <div className="text-center my-2">
                      <i className="fas fa-arrow-down fa-2x text-danger"></i>
                    </div>
                    <div>
                      <strong>To:</strong> {selectedUser.name}
                    </div>
                  </div>
                </div>
                <p className="text-danger mt-3 mb-0">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  This action will:
                </p>
                <ul className="text-danger">
                  <li>Remove creator privileges from {event.creator_name}</li>
                  <li>Grant full creator privileges to {selectedUser.name}</li>
                  <li>Preserve all existing sessions and data</li>
                  <li>Be logged in the audit log</li>
                </ul>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={transferring}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleTransfer}
                  disabled={transferring}
                >
                  {transferring ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Transferring...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-exchange-alt me-2"></i>
                      Confirm Transfer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventTransferPage;
